import { Loader2, FileDown, CheckCircle2, AlertTriangle } from "lucide-react";
import type { RefObject } from "react";
import { Button } from "@/components/ui/button";
import type { ReportSummary } from "@/pages/portal/autoscan/types/customer";
import type { LiveScanStatus } from "@/lib/live-scan";

interface LiveScanStatusPanelProps {
  status: LiveScanStatus;
  scanLog: string;
  scanSummary: ReportSummary | null;
  scanError: string | null;
  pdfAvailable: boolean;
  pdfSending?: boolean;
  pdfSent?: boolean;
  onDownloadPdf: () => void;
  logRef?: RefObject<HTMLPreElement>;
  statusPageHref?: string;
  showEmailStatus?: boolean;
}

const summaryItem = (label: string, value: number, className?: string) => (
  <div className="flex justify-between">
    <span>{label}</span>
    <span className={className || "font-semibold"}>{value}</span>
  </div>
);

export const LiveScanStatusPanel = ({
  status,
  scanLog,
  scanSummary,
  scanError,
  pdfAvailable,
  pdfSending,
  pdfSent,
  onDownloadPdf,
  logRef,
  statusPageHref,
  showEmailStatus = false,
}: LiveScanStatusPanelProps) => {
  if (status === "idle") return null;

  const isRunning = status === "running" || status === "starting";
  const isCompleted = status === "completed";
  const isError = status === "error";

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border/50 bg-muted/30 p-4 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          {isRunning && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
          {isCompleted && <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
          {isError && <AlertTriangle className="h-4 w-4 text-destructive" />}
          <span>
            {isRunning && "Live scan in progress"}
            {isCompleted && "Live scan completed"}
            {isError && "Live scan failed"}
          </span>
        </div>
        {isRunning && (
          <p className="text-xs text-muted-foreground">
            You can leave this page and return later using your scan link.
          </p>
        )}
        {isCompleted && (
          <p className="text-xs text-muted-foreground">
            Your report is ready to download.
          </p>
        )}
        {isError && scanError && (
          <p className="text-xs text-destructive">{scanError}</p>
        )}
        {statusPageHref && (
          <a
            href={statusPageHref}
            className="text-xs text-primary underline underline-offset-4"
          >
            Open status page
          </a>
        )}
      </div>

      {!isError && (
        <div className="rounded-lg border border-border/50 bg-secondary/40">
          <div className="px-4 py-2 border-b border-border/50 text-xs font-mono text-muted-foreground">
            // LIVE_VIEW
          </div>
          <pre
            ref={logRef}
            className="max-h-56 overflow-auto px-4 py-3 text-xs text-muted-foreground whitespace-pre-wrap"
          >
            {scanLog || "Live scan output will appear here."}
          </pre>
        </div>
      )}

      {!isError && scanSummary && (
        <div className="rounded-lg border border-border/50 bg-secondary/30 p-4">
          <div className="text-xs font-mono text-muted-foreground mb-3">// SUMMARY</div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {summaryItem("Total", scanSummary.totalFindings)}
            {summaryItem("Critical", scanSummary.critical, "font-semibold text-red-400")}
            {summaryItem("High", scanSummary.high, "font-semibold text-orange-400")}
            {summaryItem("Medium", scanSummary.medium, "font-semibold text-amber-300")}
            {summaryItem("Low", scanSummary.low, "font-semibold text-primary")}
            {summaryItem("Info", scanSummary.info, "font-semibold text-muted-foreground")}
          </div>
        </div>
      )}

      {!isError && (
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={onDownloadPdf}
            disabled={!pdfAvailable}
          >
            <FileDown className="mr-2 h-4 w-4" />Download PDF report
          </Button>
          {showEmailStatus && (
            <Button type="button" variant="ghost" className="w-full" disabled>
              {pdfSending ? "Emailing report..." : pdfSent ? "Report emailed" : "Report will be emailed"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
